import {BaseStage} from "./BaseStage";
import {loading, stopLoading} from "../../../helpers/loading";
import Axios from "axios";
import createElement from "../../../helpers/createElement";
import handleAjaxErrorV2 from "../../../helpers/handleAjaxErrorV2";
import {displayLocalValidationError} from "../../../helpers/forms/validationErrors/utils";
import {clearFeedbacksByName, clearFeedbacksByNameV2, fillInputsByName} from "../../../helpers/forms";
import {getSingleInputByNameV3} from "../../../helpers/forms/formInputsHelpers";
import {TotalPreviewComponent} from "../TotalPreviewComponent/TotalPreviewComponent";

class PaymentInfoStage extends BaseStage {

    /** @var {TotalPreviewComponent} */
    totalPreviewComponent;

    constructor(globalState) {
        super(globalState);

        this.domContainer = document.querySelector(`.stage.${this.getStageKey()}`)

        // Get route contents
        const getViewHtmlRoute = this.domContainer.dataset.getPayementInfoViewRoute;

        this.fields = [
            'first_name',
            'last_name',
            'phone',
            'email',
            'address1',
            'address2',
            'city',
            'state',
            'zip',
            'cc_number',
            'cc_month',
            'cc_year',
            'cc_cvv',
        ]

        this.requiredFields = [
            'first_name',
            'last_name',
            'phone',
            'email',
            'address1',
            'city',
            'state',
            'zip',
            'cc_number',
            'cc_month',
            'cc_year',
            'cc_cvv',
        ];

        if (this.globalState.isCurrentEventView) {
            // Stripe forces us to require all fields
            this.requiredFields = [
                'first_name',
                'last_name',
                'phone',
                'email',
                'address1',
                'city',
                'state',
                'zip',
                'cc_number',
                'cc_month',
                'cc_year',
                'cc_cvv',
            ];
        }

        // Skip loading if express mode is enabled
        if (!this.globalState.isExpressMode()) {
            loading();
        }


        Axios.post(
            getViewHtmlRoute,
            this.globalState.getOrderDataForServer(),
        )
            .then(response => {
                // Retrieve HTML from server
                const viewHtml = createElement(response.data.view);

                // Replace contents from server
                this.domContainer.innerHTML = viewHtml.innerHTML;

                // Run default components initializations
                this.initSharedComponents();

                this.initView();
            })
            .catch(error => {
                handleAjaxErrorV2(error)

                this.globalState.stageManager.previousStage();
                this.globalState.stageManager.clearStageInstance('payment-info');
            })
            .finally(stopLoading);

    }

    /**
     * @return {PublicTicketsFormDefs.Stage}
     */
    getStageKey() {
        // noinspection JSValidateTypes
        return 'payment-info';
    }

    initView() {
        const standardLayout = this.domContainer.querySelector('.standard-layout');
        const currentEventLayout = this.domContainer.querySelector('.current-event-layout');

        // Force current event view off temporarily
        // We forced it off because
        // A) We need to ensure required stripe details are used
        this.globalState.isCurrentEventView = false;

        if (this.globalState.isCurrentEventView) {
            // Remove standard layout, use current event layout
            standardLayout.remove()
            currentEventLayout.classList.remove('d-none');
        } else {
            // Remove non standard layouts
            if (currentEventLayout) {
                currentEventLayout.remove();
            }
        }

        const isUsingStripePaymentElements = getSingleInputByNameV3(this.domContainer, 'isUsingStripePaymentElements') === '1';
        this.globalState.setIsUsingStripePaymentElements(isUsingStripePaymentElements);

        // Init field event handlers
        this.fields.forEach(field => {
            const input = this.domContainer.querySelector(`input[name="${field}"],select[name="${field}"]`);
            if (input) {
                input.addEventListener('change', () => {
                    this.validateField(input);
                    this.updatePaymentInfo();
                });
                input.addEventListener('keyup', () => {
                    this.validateField(input);
                    this.updatePaymentInfo();
                });
                input.addEventListener('blur', () => {
                    this.validateField(input);
                })
            }

        })

        // Fill in user payment info if already done
        // TODO: Handle stripe payment form
        if (this.globalState.paymentInfo) {
            fillInputsByName(this.domContainer, this.globalState.paymentInfo);

            // Update payment info, to validate if we can continue
            // to the next stage
            this.updatePaymentInfo();
        }

        this.totalPreviewComponent = this.getTotalPreviewComponent()


        // Override default next stage button
        this.domContainer.querySelector('.next-stage-button')
            .addEventListener(
                'click',
                async e => {
                    e.stopPropagation();
                    e.preventDefault();

                    await this.handleNextStepRequest();
                },
                {
                    capture: true,
                }
            )

        this.updateTotals()
            .then(() => {
                stopLoading();

                if (this.globalState.isUsingStripePaymentElements) {
                    // Queue stripe initialization
                    const stripeKey = getSingleInputByNameV3(document.body, 'STRIPE_PUBLIC_KEY');

                    this.stripe = window.Stripe(stripeKey);
                    // Set stripe instance as we need to call it in later stages
                    this.globalState.setStripeInstance(this.stripe);

                    this.stripeElements = this.stripe.elements({
                        mode: 'payment',
                        currency: 'usd',
                        paymentMethodTypes: ['card'],
                        paymentMethodCreation: 'manual',
                        amount: Math.round((this.totalPreviewComponent.getOrderTotal() > 0 ? this.totalPreviewComponent.getOrderTotal() : 1) * 100),
                    })

                    // Create and mount the Payment Element
                    const paymentElementOptions = {
                        layout: 'accordion',
                        // Hide postal code
                        fields: {
                            billingDetails: 'never',
                        }
                    };
                    const paymentElement = this.stripeElements.create('payment', paymentElementOptions);
                    this.paymentElement = paymentElement;

                    this.stripePaymentFormContainer = this.domContainer.querySelector('.stripe-payment-form');

                    // Mount on DOM
                    paymentElement.mount(this.stripePaymentFormContainer);

                    // Remove loading overlay
                    stopLoading(this.stripePaymentFormContainer);

                    // Set correct required fields for stripe form
                    this.removeCCFields();

                    // Display previous method if available
                    this.initStripePreviousPaymentMethod();

                    // Call this to enable the button if all was prefilled correctly
                    this.updatePaymentInfo();
                }
            });
    }

    getTotalPreviewComponent() {
        return new TotalPreviewComponent(
            this.domContainer.querySelector('.totals-preview-wrapper'),
            this.globalState
        );
    }

    /**
     * @param {HTMLInputElement} fieldElement
     */
    validateField(fieldElement) {
        const fieldContainer = fieldElement.parentElement.parentElement
        const value = fieldElement.value.trim();

        clearFeedbacksByName(fieldContainer, [fieldElement.name]);

        const fieldRequired = this.requiredFields.includes(fieldElement.name);

        if (fieldRequired && value === '') {
            // Default behavior
            displayLocalValidationError(
                fieldContainer.parentElement,
                fieldElement.name,
                `${this.getFieldDisplayName(fieldElement.name)} is required`
            )

        }
    }

    /**
     * Save payment info to state
     */
    updatePaymentInfo() {
        const paymentInfo = {};

        this.fields.forEach(field => {
            let input = getSingleInputByNameV3(this.domContainer, field);
            if (input) {
                paymentInfo[field] = input.trim();
            }
        })

        // Preserve confirmation tokens if already set
        if (this.globalState.paymentInfo && this.globalState.paymentInfo.stripe_confirmation_token) {
            paymentInfo.stripe_confirmation_token = this.globalState.paymentInfo.stripe_confirmation_token;
            paymentInfo.stripe_confirmation_token_response = this.globalState.paymentInfo.stripe_confirmation_token_response;
        }

        this.globalState.setPaymentInfo(paymentInfo);

        if (this.allRequiredFieldsFilled()) {
            this.enableNextStageButton()
        } else {
            this.disableNextStageButton()
        }
    }

    allRequiredFieldsFilled() {
        let allFilled = true;

        this.requiredFields.forEach(requiredField => {
            const value = this.globalState.paymentInfo?.[requiredField];

            if (!value) {
                allFilled = false;
            }
        })

        return allFilled;
    }

    getFieldDisplayName(field) {
        const fieldDisplayNameMap = {
            first_name: 'First Name',
            last_name: 'Last Name',
            phone: 'Phone',
            email: 'Email',
            address1: 'Address',
            address2: 'Address',
            city: 'City',
            state: 'State',
            zip: 'Zip',
            cc_number: 'Credit card number',
            cc_month: 'Credit card month',
            cc_year: 'Credit card year',
            cc_cvv: 'Credit card cvv',

        };

        return fieldDisplayNameMap[field];
    }

    updateTotals() {
        if (!this.totalPreviewComponent) {
            // Preview is not yet ready
            return Promise.resolve();
        }

        return this.totalPreviewComponent.updateTotalsFromServer();
    }

    removeCCFields() {
        const ccFields = [
            'cc_number',
            'cc_month',
            'cc_year',
            'cc_cvv',
        ];

        // Remove CC fields
        this.requiredFields = this.requiredFields.filter(field => {
            return !ccFields.includes(field);
        });
    }

    /**
     * Determines if Stripe Payment Elements logic and validation should be active
     * for the current checkout attempt.
     *
     * @return {boolean}
     */
    isStripePaymentRequired() {
        return this.globalState.isUsingStripePaymentElements;
    }

    /**
     * @return {Promise<boolean>}
     */
    async handleNextStepRequest() {
        if (this.allRequiredFieldsFilled()) {
            if (this.isStripePaymentRequired()) {
                clearFeedbacksByNameV2(this.stripePaymentFormContainer.parentElement);

                // If the stripe method form is visible
                // we WILL get a new token always
                if (this.stripePaymentFormContainer.classList.contains('d-none') === false) {
                    try {
                        loading();
                        await this.getStripeConfirmationToken();
                    } catch (error) {
                        console.log({stripeError: error});
                        displayLocalValidationError(
                            this.domContainer,
                            '_',
                            error.message
                        )
                    }

                    stopLoading();
                }


                // If we still don't have it, fail and let the user know
                if (!this.globalState.paymentInfo.stripe_confirmation_token) {
                    displayLocalValidationError(
                        this.domContainer,
                        'stripe_confirmation_token',
                        'Please fill in credit card details'
                    )
                    return false;
                }
            }

            this.globalState.stageManager.nextStage();
            return true;
        } else {
            displayLocalValidationError(this.domContainer, '_', 'Please fill in all required fields')
            return false;
        }
    }

    async getStripeConfirmationToken() {
        // Create confirmation token from card form
        const paymentInfo = {...this.globalState.paymentInfo};

        // Trigger form validation and wallet collection
        const {error: submitError} = await this.stripeElements.submit();
        if (submitError) {
            throw submitError;
        }

        // Get confirmation token
        const {error, confirmationToken} = await this.stripe.createConfirmationToken({
            elements: this.stripeElements,
            params: {
                payment_method_data: {
                    billing_details: {
                        name: `${paymentInfo.first_name} ${paymentInfo.last_name}`.trim(),
                        email: paymentInfo.email,
                        phone: paymentInfo.phone,
                        address: {
                            line1: paymentInfo.address1,
                            city: paymentInfo.city,
                            state: paymentInfo.state,
                            country: 'US',
                            postal_code: paymentInfo.zip,
                        },
                    },
                }
            }
        });

        if (error) {
            throw error;
        }

        paymentInfo.stripe_confirmation_token = confirmationToken.id;
        paymentInfo.stripe_confirmation_token_response = confirmationToken;

        this.globalState.setPaymentInfo(paymentInfo);
    }

    initStripePreviousPaymentMethod() {
        const paymentInfo = this.globalState.paymentInfo;

        if (!paymentInfo || !paymentInfo.stripe_confirmation_token) {
            // Do nothing
            return;
        }

        const previousPaymentMethodContainer = this.domContainer.querySelector('.stripe-existing-payment-method');
        if (!previousPaymentMethodContainer) {
            return;
        }

        // Update last 4
        previousPaymentMethodContainer.querySelector('.card-last-4')
            .textContent = paymentInfo.stripe_confirmation_token_response.payment_method_preview.card.last4;
        // Set event handler
        previousPaymentMethodContainer.querySelector('.edit-payment-method')
            .addEventListener('click', () => {
                this.removeStripePaymentMethod();
            });

        // Make visible
        previousPaymentMethodContainer.classList.remove('d-none');
        // Hide stripe payment method form
        this.stripePaymentFormContainer.classList.add('d-none')
        this.stripePaymentFormContainer.classList.remove('d-flex')

    }


    removeStripePaymentMethod() {
        // Make form visible
        this.stripePaymentFormContainer.classList.remove('d-none')
        this.stripePaymentFormContainer.classList.add('d-flex')
        // Hide previous payment method container
        const previousPaymentMethodContainer = this.domContainer.querySelector('.stripe-existing-payment-method');
        if (previousPaymentMethodContainer) {
            previousPaymentMethodContainer.classList.add('d-none');
        }

        // Clear previous one from memory
        const paymentInfo = {...this.globalState.paymentInfo};

        paymentInfo.stripe_confirmation_token = undefined;
        paymentInfo.stripe_confirmation_token_response = undefined;

        this.globalState.setPaymentInfo(paymentInfo);

    }


    clearDom() {
        if (this.paymentElement) {
            this.paymentElement.unmount();
            this.paymentElement.destroy();
            this.paymentElement = null;
        }

        if (this.stripeElements) {
            // Destroying the element is usually enough, but we can null this out
            this.stripeElements = null;
        }
    }
}

export {PaymentInfoStage};
